/**
 * NotesSection - CRUD de notas + recordatorios
 * Sub-componente presentacional extraído de DayEditModal
 */
import { Check, Loader2, MessageSquare, Clock, Bell, Heart, Briefcase, Star, Pencil, Trash2, ChevronDown, ChevronUp } from 'lucide-react';

const NOTE_TYPE_CONFIG = {
    medico: { icon: Heart, color: 'text-red-400', bg: 'bg-red-500/10' },
    laboral: { icon: Briefcase, color: 'text-amber-400', bg: 'bg-amber-500/10' },
    personal: { icon: Star, color: 'text-purple-400', bg: 'bg-purple-500/10' },
    general: { icon: MessageSquare, color: 'text-cyan-400', bg: 'bg-cyan-500/10' }
};

const NOTE_TYPES = [
    { id: 'general', label: 'General', icon: MessageSquare, color: 'text-cyan-600 dark:text-cyan-400', bg: 'bg-cyan-500/10' },
    { id: 'medico', label: 'Médico', icon: Heart, color: 'text-red-600 dark:text-red-400', bg: 'bg-red-500/10' },
    { id: 'laboral', label: 'Laboral', icon: Briefcase, color: 'text-amber-600 dark:text-amber-400', bg: 'bg-amber-500/10' },
    { id: 'personal', label: 'Personal', icon: Star, color: 'text-purple-600 dark:text-purple-400', bg: 'bg-purple-500/10' }
];

export default function NotesSection({
    showComentarioSection,
    setShowComentarioSection,
    notas,
    loadingNotas,
    noteText,
    setNoteText,
    selectedTipo,
    setSelectedTipo,
    editingNotaId,
    setEditingNotaId,
    noteReminderEnabled,
    setNoteReminderEnabled,
    noteReminderHora,
    setNoteReminderHora,
    reminderEnabled,
    setReminderEnabled,
    reminderHora,
    setReminderHora,
    reminderNotificado,
    setReminderNotificado,
    handleEditNota,
    handleDeleteNota
}) {
    return (
        <div className={`border rounded-xl overflow-hidden ${reminderEnabled ? 'border-amber-500/30' : 'border-gray-500/20'}`}>
            <button
                id="tour-edit-comentario"
                onClick={() => setShowComentarioSection(!showComentarioSection)}
                className={`w-full flex items-center justify-between p-3 transition-colors ${(notas.length > 0 || reminderEnabled) ? 'bg-amber-500/10 hover:bg-amber-500/20' : 'bg-gray-500/10 hover:bg-gray-500/20'}`}
            >
                <span className={`flex items-center gap-2 font-bold ${(notas.length > 0 || reminderEnabled) ? 'text-amber-600 dark:text-amber-400' : 'text-[var(--text-muted)]'}`}>
                    <MessageSquare className="w-4 h-4" />
                    Notas
                    {notas.length > 0 && <span className="text-[10px] bg-cyan-500/20 text-cyan-700 dark:text-cyan-400 px-2 py-0.5 rounded-full uppercase">{notas.length} notas</span>}
                    {noteText.trim() && <span className="text-[10px] bg-gray-500/20 text-gray-700 dark:text-gray-400 px-2 py-0.5 rounded-full uppercase">Borrador</span>}
                    {reminderEnabled && <span className="text-[10px] bg-amber-500/20 text-amber-700 dark:text-amber-400 px-2 py-0.5 rounded-full flex items-center gap-1 uppercase"><Bell className="w-3 h-3" />Recordatorio</span>}
                </span>
                {showComentarioSection ? <ChevronUp className="w-4 h-4 text-[var(--text-muted)]" /> : <ChevronDown className="w-4 h-4 text-[var(--text-muted)]" />}
            </button>

            {showComentarioSection && (
                <div className="p-3 space-y-4">

                    {/* Lista de Notas Existentes */}
                    {loadingNotas ? (
                        <div className="flex justify-center p-2"><Loader2 className="w-4 h-4 text-cyan-400 animate-spin" /></div>
                    ) : notas.length > 0 ? (
                        <div className="space-y-2 mb-2">
                            {notas.map(nota => {
                                const config = NOTE_TYPE_CONFIG[nota.tipo] || NOTE_TYPE_CONFIG.general;
                                const TypeIcon = config.icon;

                                return (
                                    <div key={nota.id} className="group flex items-start justify-between p-3 rounded-2xl border transition-all" style={{ background: 'var(--bg-input)', borderColor: 'var(--border-color)' }}>
                                        <div className="flex-1 pr-3">
                                            <div className="flex items-center gap-2 mb-1">
                                                <TypeIcon className={`w-3 h-3 ${config.color}`} />
                                                <span className={`text-[10px] font-bold uppercase tracking-wider ${config.color}`}>{nota.tipo || 'General'}</span>
                                                <span className="text-[10px] text-[var(--text-secondary)]">
                                                    {nota.created_at ? new Date(nota.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '--:--'}
                                                </span>
                                                {nota.reminder_time && (
                                                    <span className="flex items-center gap-1 text-[10px] text-amber-500 font-bold ml-1">
                                                        <Bell className="w-2.5 h-2.5" /> {nota.reminder_time.substring(0, 5)}
                                                    </span>
                                                )}
                                            </div>
                                            <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{nota.comentario}</p>
                                        </div>
                                        <div className="flex gap-1">
                                            <button
                                                onClick={() => handleEditNota(nota)}
                                                className="text-[var(--text-secondary)] opacity-100 lg:opacity-0 lg:group-hover:opacity-100 hover:text-cyan-400 transition-all p-1"
                                                title="Editar nota"
                                            >
                                                <Pencil className="w-3.5 h-3.5" />
                                            </button>
                                            <button
                                                onClick={() => handleDeleteNota(nota.id)}
                                                className="text-[var(--text-secondary)] opacity-100 lg:opacity-0 lg:group-hover:opacity-100 hover:text-red-400 transition-all p-1"
                                                title="Eliminar nota"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : null}

                    {/* Nueva Nota Rápida */}
                    <div className="space-y-3">
                        <div>
                            <label className="text-[11px] font-medium text-[var(--text-muted)] uppercase tracking-wider mb-2 block">Tipo de nota</label>
                            <div className="grid grid-cols-4 gap-2">
                                {NOTE_TYPES.map((t) => (
                                    <button
                                        key={t.id}
                                        onClick={(e) => { e.preventDefault(); setSelectedTipo(t.id); }}
                                        className={`flex flex-col items-center justify-center p-2 rounded-xl border transition-all ${selectedTipo === t.id
                                            ? `ring-1 ring-cyan-400 ${t.bg} border-transparent`
                                            : 'border-transparent grayscale opacity-60 hover:opacity-100 hover:grayscale-0'
                                            }`}
                                        style={{ background: selectedTipo === t.id ? 'rgba(34, 211, 238, 0.1)' : 'var(--bg-input)' }}
                                    >
                                        <t.icon className={`w-4 h-4 mb-2 ${selectedTipo === t.id ? t.color : 'text-[var(--text-muted)]'}`} />
                                        <span className={`text-[9px] font-black uppercase tracking-tighter ${selectedTipo === t.id ? 'text-inherit' : 'text-[var(--text-muted)]'}`}>{t.label}</span>
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div>
                            <div className="flex items-center justify-between mb-1">
                                <label className="text-[11px] font-medium text-[var(--text-muted)] uppercase tracking-wider block">
                                    {editingNotaId ? 'Editando nota' : 'Añadir nueva nota'}
                                </label>
                                {editingNotaId && (
                                    <button
                                        onClick={() => {
                                            setEditingNotaId(null);
                                            setNoteText('');
                                            setNoteReminderEnabled(false);
                                        }}
                                        className="text-[10px] text-cyan-400 hover:underline"
                                    >
                                        Cancelar edición
                                    </button>
                                )}
                            </div>
                            <textarea
                                value={noteText}
                                onChange={(e) => setNoteText(e.target.value)}
                                placeholder="Escribe algo importante para este día..."
                                rows={2}
                                className="w-full px-4 py-3 rounded-xl border resize-none focus:outline-none focus:border-cyan-500 transition-colors"
                                style={{ background: 'var(--bg-input)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
                            />
                        </div>

                        {/* Recordatorio por nota */}
                        <div className="flex items-center justify-between p-3 rounded-xl bg-amber-500/5 border border-amber-500/10">
                            <div className="flex items-center gap-2">
                                <Bell className={`w-4 h-4 ${noteReminderEnabled ? 'text-amber-600 dark:text-amber-400' : 'text-[var(--text-muted)]'}`} />
                                <span className="text-xs font-bold" style={{ color: 'var(--text-secondary)' }}>Avisarme a esta hora</span>
                            </div>
                            <div className="flex items-center gap-2">
                                {noteReminderEnabled && (
                                    <input
                                        type="time"
                                        value={noteReminderHora}
                                        onChange={(e) => setNoteReminderHora(e.target.value)}
                                        className="px-2 py-1 rounded bg-amber-500/20 border border-amber-500/30 text-amber-400 text-[11px] focus:outline-none"
                                    />
                                )}
                                <button
                                    type="button"
                                    onClick={() => setNoteReminderEnabled(!noteReminderEnabled)}
                                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${noteReminderEnabled ? 'bg-amber-500' : 'bg-gray-600'}`}
                                >
                                    <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${noteReminderEnabled ? 'translate-x-5' : 'translate-x-1'}`} />
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Reminder Toggle */}
                    <div className="pt-2 border-t border-white/10">
                        <div className="flex items-center justify-between">
                            <label className="flex items-center gap-2 text-sm font-bold cursor-pointer" style={{ color: 'var(--text-secondary)' }}>
                                <Bell className={`w-4 h-4 ${reminderEnabled ? 'text-amber-600 dark:text-amber-400' : 'text-[var(--text-muted)]'}`} />
                                Recordarme este día
                            </label>
                            <button
                                type="button"
                                onClick={() => setReminderEnabled(!reminderEnabled)}
                                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${reminderEnabled ? 'bg-amber-500' : 'bg-gray-600'
                                    }`}
                            >
                                <span
                                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${reminderEnabled ? 'translate-x-6' : 'translate-x-1'
                                        }`}
                                />
                            </button>
                        </div>

                        {/* Time Picker or Sent Badge */}
                        {reminderEnabled && (
                            <div className="mt-3 flex items-center gap-3">
                                {reminderNotificado ? (
                                    <>
                                        <Check className="w-4 h-4 text-green-400" />
                                        <span className="text-sm text-green-400 font-medium">✓ Enviado a las {reminderHora}</span>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setReminderNotificado(false);
                                                setReminderHora('08:00');
                                            }}
                                            className="text-xs text-[var(--text-secondary)] hover:text-gray-300 underline ml-2"
                                        >
                                            Reprogramar
                                        </button>
                                    </>
                                ) : (
                                    <>
                                        <Clock className="w-4 h-4 text-amber-400" />
                                        <span className="text-sm text-[var(--text-muted)]">Hora del recordatorio:</span>
                                        <input
                                            type="time"
                                            value={reminderHora}
                                            onChange={(e) => setReminderHora(e.target.value)}
                                            className="px-3 py-1.5 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-400 text-sm focus:outline-none focus:border-amber-500"
                                        />
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
